import i18n from '@/core/i18n';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers/status-options.mapper';
import { IntegrationState } from '@/modules/common/types/integrations';

/**
 * Integration status classes that implement the BaseStatus interface.
 * Each status knows how to configure itself for the StatusBadge component.
 */
export class IntegrationStatusConnected implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-green-600 text-green-600',
    };
  }
}

export class IntegrationStatusSyncing implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-green-600 text-green-600',
    };
  }
}

export class IntegrationStatusError implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-destructive text-destructive',
    };
  }
}

export class IntegrationStatusNotConnected implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

export class IntegrationStatusUnknown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

// Factory function to create status instances based on enum values
export const createIntegrationStatus = (status: IntegrationState): BaseStatus => {
  switch (status) {
    case IntegrationState.CONNECTED:
      return new IntegrationStatusConnected();
    case IntegrationState.SYNCING:
      return new IntegrationStatusSyncing();
    case IntegrationState.ERROR_CONNECTION:
      return new IntegrationStatusError();
    case IntegrationState.NOT_CONNECTED:
      return new IntegrationStatusNotConnected();
    default:
      return new IntegrationStatusUnknown();
  }
};

/**
 * Get localized status text for integration status.
 */
export const getIntegrationStatusText = (status: IntegrationState): string => {
  switch (status) {
    case IntegrationState.CONNECTED:
    case IntegrationState.SYNCING:
      return i18n.t('authenticated.settings:pages.integrations.table.status.connected');
    case IntegrationState.ERROR_CONNECTION:
      return i18n.t('authenticated.settings:pages.integrations.table.status.error');
    case IntegrationState.NOT_CONNECTED:
      return i18n.t('authenticated.settings:pages.integrations.table.status.notConnected');
    default:
      return i18n.t('authenticated.settings:pages.integrations.table.status.notConnected');
  }
};

export const getIntegrationStatusColor = (status: IntegrationState): string => {
  switch (status) {
    case IntegrationState.CONNECTED:
    case IntegrationState.SYNCING:
      return 'green-600';
    case IntegrationState.ERROR_CONNECTION:
      return 'destructive';
    case IntegrationState.NOT_CONNECTED:
      return 'muted-foreground';
    default:
      return 'muted-foreground';
  }
};

/**
 * Get badge variant for integration status (for backward compatibility with Badge component).
 */
export const getIntegrationStatusBadgeVariant = (
  status: IntegrationState
): 'default' | 'secondary' | 'outline' | 'destructiveOutline' | 'success2Outline' => {
  switch (status) {
    case IntegrationState.CONNECTED:
    case IntegrationState.SYNCING:
      return 'success2Outline';
    case IntegrationState.ERROR_CONNECTION:
      return 'destructiveOutline';
    case IntegrationState.NOT_CONNECTED:
      return 'outline';
    default:
      return 'outline';
  }
};

/**
 * Preconfigured mapper for integration status options.
 * Maps an array of IntegrationState values to option objects with label, value, and color.
 */
export const getIntegrationStatusOptions = createStatusOptionsMapper<IntegrationState>({
  getStatusColor: getIntegrationStatusColor,
  getStatusText: getIntegrationStatusText,
});
